import '../styles/globals.css';
import { RootErrorBoundary } from '@anchorage/error-boundary';
import { ApolloClientProvider, ErrorNotifier } from '@anchorage/graphql/client';
import { LocalStorageProvider, usePortoTheme } from '@anchorage/hooks';
import { ClientReadyGate, useFontSetup } from '@anchorage/next';
import { handleError } from '@anchorage/sentry';
// https://nextjs.org/docs/app/api-reference/components/font#css-variables
// Levering next/font to avoid CSP issues when downloading google fonts
import { configureSpriteUrl } from '@latitude/icons';
import { Toaster, ToasterProvider, useToast } from '@latitude/toast';
import { TooltipProvider } from '@latitude/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AnalyticsPageViewTracker } from 'components/AnalyticsPageViewTracker/AnalyticsPageViewTracker';
import AnalyticsWrapper from 'components/AnalyticsWrapper';
import { DeviceChecker } from 'components/DeviceChecker/DeviceChecker';
import DevToolsDrawer from 'components/DevToolsDrawer';
import { PORTO_RECAPTCHA_V3_SITE_KEY } from 'constants/app';
import { AppContextProvider as AppContextProviderNew } from 'context/AppContext/AppContext';
import FeatureFlagsProvider from 'context/FeatureFlagsProvider';
import useInfoDrawer from 'hooks/useInfoDrawer';
import type { AppProps } from 'next/app';
import { Inter, Roboto_Mono } from 'next/font/google';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { getSessionInfo, kycModeHeaders } from 'utils/apiUtils';
import { ONBOARDING_TRACING_URLS } from 'utils/datadogTracingUrls';
import useIsPortoApp from 'utils/useIsPortoApp';

import 'normalize.css';

configureSpriteUrl(`${process.env.PATH_PREFIX || ''}/icon-sprite/sprite.svg`);

const inter = Inter({
  subsets: ['latin'],
  display: 'swap',
  preload: true,
  adjustFontFallback: false,
});
const roboto = Roboto_Mono({
  subsets: ['latin'],
  display: 'swap',
  preload: true,
  adjustFontFallback: false,
});

const queryClient = new QueryClient();

const getApolloConfig = () => {
  const { id, sessionMode } = getSessionInfo();
  return {
    uri: `/graphql/${encodeURIComponent(id)}`,
    headers: kycModeHeaders(sessionMode),
    name: 'onboarding',
    version: '1',
  };
};

// ErrorNotifier takes an injected toast fn; call useToast inside ToasterProvider
// and pass it through so @anchorage/graphql needs no @latitude/toast dependency.
function ErrorNotifierWithToast() {
  const { toast } = useToast();
  return <ErrorNotifier toast={toast} />;
}

function MyApp({ Component, pageProps }: AppProps) {
  const { basePath, asPath, replace } = useRouter();

  const sessionInfo = getSessionInfo();

  const isPorto = useIsPortoApp();
  const apolloConfig = useMemo(() => getApolloConfig(), []);

  const { component: InfoDrawer } = useInfoDrawer();

  const [datadogInitialized, setDatadogInitialized] = useState(false);

  usePortoTheme({
    active: isPorto,
  });

  const faviconPath = useMemo(() => {
    return `${basePath}/${isPorto ? 'portoFavicon.ico' : 'favicon.ico'}`;
  }, [isPorto, basePath]);

  useEffect(() => {
    // redirect to home if it's not home
    if (isPorto && asPath !== '/') {
      replace('/');
    }
  }, [isPorto, asPath, replace]);

  useFontSetup({
    '--font-inter': inter.style.fontFamily,
    '--font-roboto-mono': roboto.style.fontFamily,
  });

  useEffect(() => {
    const appWindowEnv = typeof window === 'undefined' ? null : window.env;

    if (datadogInitialized || !appWindowEnv) {
      return;
    }

    const {
      DATADOG_APPLICATION_ID: applicationId,
      DATADOG_CLIENT_TOKEN: clientToken,
      DATADOG_SITE: site,
      DATADOG_SERVICE: service,
      DATADOG_ENVIRONMENT: env,
    } = appWindowEnv;

    if (applicationId && clientToken) {
      // Dynamically imported so @datadog/browser-rum lands in its own chunk
      // instead of the main _app bundle.
      import('@anchorage/datadog-rum')
        .then(
          ({
            ANCHORAGE_TRACING_URLS,
            anchorageBeforeSend,
            initDatadogRum,
            withSecretRedacted,
          }) => {
            initDatadogRum({
              applicationId,
              clientToken,
              site,
              service,
              env,
              allowedTracingUrls: [
                ...ANCHORAGE_TRACING_URLS,
                ...ONBOARDING_TRACING_URLS,
              ],
              beforeSend: withSecretRedacted(
                getSessionInfo().access,
                anchorageBeforeSend,
              ),
            });
          },
        )
        .catch(handleError);
    }

    setDatadogInitialized(true);
  }, [datadogInitialized]);

  return (
    <>
      <Head>
        <link id="favicon" rel="shortcut icon" href={faviconPath} />
      </Head>
      <LocalStorageProvider errorHandler={handleError}>
        <ApolloClientProvider config={apolloConfig}>
          <QueryClientProvider client={queryClient}>
            <GoogleReCaptchaProvider
              reCaptchaKey={isPorto ? PORTO_RECAPTCHA_V3_SITE_KEY : ''}
              useEnterprise={true}
            >
              <TooltipProvider delayDuration={300} skipDelayDuration={500}>
                <ToasterProvider>
                  <ErrorNotifierWithToast />
                  <AppContextProviderNew sessionInfo={sessionInfo}>
                    <AnalyticsWrapper>
                      <ClientReadyGate>
                        <FeatureFlagsProvider trafficKey={sessionInfo.id}>
                          <AnalyticsPageViewTracker>
                            <div className="flex">
                              {/*
                               * Catches render-phase errors in page content
                               * so they reach Sentry with a real stack
                               * instead of falling through to _error as
                               * "Error undefined" (static-export SPA).
                               */}
                              <RootErrorBoundary resetKeys={[asPath]}>
                                <DeviceChecker>
                                  <Component {...pageProps} />
                                </DeviceChecker>
                              </RootErrorBoundary>
                              <div id="modal-root" />
                              <div id="drawer-root" />
                              <Toaster />
                            </div>
                            {InfoDrawer}
                            <DevToolsDrawer />
                          </AnalyticsPageViewTracker>
                        </FeatureFlagsProvider>
                      </ClientReadyGate>
                    </AnalyticsWrapper>
                  </AppContextProviderNew>
                </ToasterProvider>
              </TooltipProvider>
            </GoogleReCaptchaProvider>
          </QueryClientProvider>
        </ApolloClientProvider>
      </LocalStorageProvider>
    </>
  );
}

export default MyApp;
